import React, { useEffect, useState } from 'react';
import { string, number, bool } from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
    Box,
    ButtonGroup,
    Card,
    DaButton,
    Image,
    InternalGrid,
    OutIcon,
    Text,
    SkeletonItem,
    ThemeDefault,
    ModalControl,
} from '@paygreen/paygreen-ui';
import ClimateSeed from '../../assets/logos/climateSeed.svg';

import forestProjectImage from '../../assets/images/forestProject.jpg';
import { IsMobile, IsDesktop } from '../../utils/tools';
import OddModal from './OddModal';
import { CheckIconStyled } from './Project.styled';

import dayjs from '../../utils/dayjsHelper';
import i18n from '../../internationalization/i18n';

import { frLangLocale } from '../../utils/constants';

const ProjectCardDetail = ({
    t,
    isLoading,
    setIsLoading,
    isOpen,
    fetchData,
    project: {
        sustainableDevelopmentGoals,
        description,
        url,
        brokerName,
        name,
        idProject,
        isDefault,
        userSelectedSDG,
        isUserProject,
    },
    width,
}) => {
    useEffect(() => {
        if (sustainableDevelopmentGoals && description && url && brokerName) {
            setIsLoading(false);
        }
    }, [
        description,
        url,
        brokerName,
        sustainableDevelopmentGoals,
        setIsLoading,
    ]);

    const isMobile = IsMobile();
    const isDesktop = IsDesktop();
    const [imageLoaded, setIsImageLoaded] = useState(true);
    const [isLogoLoaded, setIsLogoLoaded] = useState(true);

    const handleLoadImage = () => {
        setIsImageLoaded(false);
    };

    const handleLoadLogo = () => {
        setIsLogoLoaded(false);
    };

    const renderToltipTitle = (goal) => {
        const userLanguage = dayjs.locale(frLangLocale.includes(i18n.language));

        if (userLanguage === 'fr') {
            return `${t('contributions.globalView.number')} - ${goal.id} ${
                goal.nameFrench
            }`;
        }
        return `${t('contributions.globalView.number')} - ${goal.id} ${
            goal.nameEnglish
        }`;
    };

    const renderLogo = () => {
        return sustainableDevelopmentGoals?.map((goal) => {
            return (
                <SkeletonItem
                    colorWab="grey10"
                    radiusSize="sm"
                    isLoading={imageLoaded}
                    key={goal.id}
                >
                    <Image
                        blockHeight="tiny"
                        blockWidth="tiny"
                        style={{
                            width: '46px',
                            height: '46px',
                            cursor: 'pointer',
                            position: 'relative',
                            padding: 5,
                        }}
                        imageType="cover"
                        marginBottom="sm"
                        title={renderToltipTitle(goal)}
                    >
                        <ModalControl
                            hasOnClickControl={
                                isUserProject || !!parseInt(isDefault)
                            }
                        >
                            <img
                                onLoad={handleLoadImage}
                                src={goal.iconUri}
                                alt="project view"
                            />
                        </ModalControl>

                        {userSelectedSDG === goal.id && (
                            <CheckIconStyled
                                isActive
                                hasBackground={true}
                                colorPallet="status"
                                colorStatus="success"
                                iconSize="xs"
                            />
                        )}
                    </Image>

                    <ModalControl hasOnClickControl>
                        <OddModal
                            name={name}
                            isOpen={isOpen}
                            goals={sustainableDevelopmentGoals}
                            idProject={idProject}
                            selectedSDG={userSelectedSDG}
                            fetchData={fetchData}
                        />
                    </ModalControl>
                </SkeletonItem>
            );
        });
    };

    const renderBroker = (value) => {
        switch (value) {
            case 'ClimateSeed':
                return ClimateSeed;

            default:
                return null;
        }
    };

    return (
        <>
            {width > ThemeDefault.breakpoint.md && (
                <Box marginRight="lg">{renderLogo()}</Box>
            )}

            <Box>
                <SkeletonItem
                    colorWab="grey10"
                    radiusSize="sm"
                    isLoading={isLoading}
                >
                    <Card
                        blockWidth="lg"
                        paddingBottom="sm"
                        hasBackground={false}
                    >
                        <Text textSize={isMobile ? 'xs' : 'sm'}>
                            {description}
                        </Text>
                    </Card>
                </SkeletonItem>
                <SkeletonItem
                    colorWab="grey10"
                    radiusSize="sm"
                    isLoading={isLoading}
                >
                    <Image
                        blockHeight={!isDesktop ? 'auto' : 'xs'}
                        blockWidth="auto"
                        className="gray-filter"
                        radiusSize="lg"
                        imageType="picture"
                    >
                        <img
                            style={{ width: '100%' }}
                            src={forestProjectImage}
                            alt="project view"
                        />
                    </Image>
                </SkeletonItem>
                <ButtonGroup marginTop="xs">
                    <a target="_blank" rel="noopener noreferrer" href={url}>
                        <DaButton
                            buttonSize="md"
                            buttonStyle="line"
                            gradient="theme"
                            colorTheme="secondary"
                            icon={<OutIcon />}
                        >
                            {t(
                                'contributions.carbonProjects.projectCard.website',
                            )}
                        </DaButton>
                    </a>
                </ButtonGroup>
                {renderBroker(brokerName) && (
                    <InternalGrid alignItems="center" justifyContent="center">
                        <SkeletonItem
                            colorWab="grey10"
                            radiusSize="sm"
                            blockWidth="auto"
                            blockHeight="auto"
                            isLoading={isLogoLoaded}
                        >
                            <Image
                                marginTop="lg"
                                justifyContent="center"
                                blockHeight="auto"
                                blockWidth={isMobile ? 'auto' : 'xs'}
                                className="gray-filter"
                                radiusSize="lg"
                                imageType="picture"
                            >
                                <img
                                    style={{ width: '100%' }}
                                    src={renderBroker(brokerName)}
                                    alt="project view"
                                    onLoad={handleLoadLogo}
                                />
                            </Image>
                        </SkeletonItem>
                    </InternalGrid>
                )}

                {width < ThemeDefault.breakpoint.md && (
                    <InternalGrid
                        childrenMarginTop="md"
                        justifyContent="center"
                    >
                        {renderLogo()}
                    </InternalGrid>
                )}
            </Box>
        </>
    );
};

ProjectCardDetail.propTypes = {
    blockWidth: string,
    carbonPrice: number,
    carbonOffsetValue: number,
    isCompleted: bool,
    isUserActiveProject: bool,
};

ProjectCardDetail.defaultProps = {
    carbonPrice: 0,
    carbonOffsetValue: 0,
    isCompleted: false,
    isUserActiveProject: false,
};

export default withTranslation('common', 'countries')(ProjectCardDetail);
